import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Paintbrush, Lock, CloudOff, CheckCircle2, AlertTriangle } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import colourService, {
  recordPainting, flushPaintingQueue, queuedPaintings, subscribePaintingQueue, dropQueuedPainting, type QueuedPainting,
} from '../../services/nurseryColour';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatDateInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { compareBlocks, compareSlots, rowLabel } from '../../utils/nurseryRowSlots';
import { cn } from '../../utils/cn';
import { BAND_NAME, type LotBand, type PaintWalk, type PaintWalkLot } from '../../types/nurseryColour';
import type { StocktakePlace } from '../../types/nurseryStocktake';

/**
 * /nursery/painting — walk the rows and paint the bands.
 *
 * Each lot shows the bands its line should carry (bottom, top, root) in the
 * scheme's colours. Tap a band once it is on the trees. What is recorded is
 * the paint as it went on, so a colour changed in the scheme later shows here
 * as "repaint" rather than quietly claiming the trees already carry it.
 * hh.factree's colour_coding.asp, which kept only the date.
 *
 * Taps made with no signal wait on the phone and go when it returns.
 */

const treeName = (l: PaintWalkLot) => [l.cultivar_name, l.rootstock_name].filter(Boolean).join(' / ');
const placeName = (l: PaintWalkLot) => (l.sequence_number != null ? `Position ${l.sequence_number}` : (l.location_name || 'No position'));
const sectionKey = (l: PaintWalkLot) =>
  l.row_number != null ? `${l.block_id}:${l.row_number}:${l.row_side ?? ''}` : `${l.block_id}:loc:${l.location_id}`;
const sectionTitle = (l: PaintWalkLot) =>
  [l.block_name, l.row_number != null ? rowLabel(l.row_number, l.row_side) : l.location_name].filter(Boolean).join(' · ') || 'Elsewhere';

function walkOrder(a: PaintWalkLot, b: PaintWalkLot) {
  return compareBlocks({ name: a.block_name, sort_order: a.block_sort_order }, { name: b.block_name, sort_order: b.block_sort_order })
    || compareSlots(a, b)
    || a.lot_id - b.lot_id;
}

const needsPaint = (l: PaintWalkLot) => l.bands.some((b) => b.state === 'to_paint' || b.state === 'repaint');

interface BandButtonProps {
  band: LotBand;
  queued: QueuedPainting | undefined;
  tz: string;
  onPaint: () => Promise<void>;
  onUndo: () => Promise<void>;
}

function BandButton({ band, queued, tz, onPaint, onUndo }: BandButtonProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (e) { setError(errorMessage(e, 'Could not save')); } finally { setBusy(false); }
  };
  const waiting = queued && !queued.refused;
  const paint = band.paint;
  if (!paint) {
    // Painted, but the scheme no longer asks for this band.
    return (
      <div className="text-xs text-neutral-500 break-words">
        {BAND_NAME[band.band]}: {band.painting?.paint_name} on, no longer in the scheme
      </div>
    );
  }
  const done = band.state === 'painted' || waiting;
  return (
    <div className="space-y-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy || done}
          onClick={() => run(onPaint)}
          aria-label={`${BAND_NAME[band.band]} band, ${paint.name}${done ? ', painted' : ''}`}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium max-w-full',
            done ? 'border-green-200 bg-green-50 text-green-800' : 'border-neutral-300 bg-white text-neutral-800 hover:border-primary-400',
          )}
        >
          <span className="inline-block h-4 w-4 flex-shrink-0 rounded-sm border border-black/20" style={{ backgroundColor: paint.hex }} />
          <span className="truncate">{BAND_NAME[band.band]} · {paint.name}</span>
          {done && !waiting && <CheckCircle2 className="h-4 w-4 flex-shrink-0" />}
          {waiting && <CloudOff className="h-4 w-4 flex-shrink-0 text-amber-700" />}
        </button>
        {band.state === 'painted' && band.painting && (
          <button type="button" className="text-xs text-neutral-500 underline" disabled={busy} onClick={() => run(onUndo)}>
            Undo
          </button>
        )}
      </div>
      {band.state === 'painted' && band.painting && (
        <p className="text-xs text-neutral-500 break-words">
          {formatDateInTz(band.painting.painted_at, tz)}{band.painting.painted_by_name ? ` by ${band.painting.painted_by_name}` : ''}
        </p>
      )}
      {band.state === 'repaint' && band.painting && (
        <p className="text-xs text-amber-800 break-words">
          {band.painting.paint_name} went on {formatDateInTz(band.painting.painted_at, tz)}; the line is now {paint.name}.
        </p>
      )}
      {waiting && <p className="text-xs text-amber-800">Waiting for signal</p>}
      {queued?.refused && (
        <p className="text-xs text-red-700 break-words">
          Not saved: {queued.refused}{' '}
          <button type="button" className="underline" onClick={() => dropQueuedPainting(queued.payload.client_uuid)}>Dismiss</button>
        </p>
      )}
      {error && <p className="text-xs text-red-700 break-words">{error}</p>}
    </div>
  );
}

function Painting() {
  const tz = useOrgTimezone();
  const [params, setParams] = useSearchParams();
  const farmId = Number(params.get('farm')) || null;
  const blockId = Number(params.get('block')) || null;
  const { hasAccess, loading: licenseLoading } = useModuleLicense('nursery', ['operator', 'manager', 'admin']);
  const [places, setPlaces] = useState<StocktakePlace[] | null>(null);
  const [pickFarm, setPickFarm] = useState<number | ''>('');
  const [pickBlock, setPickBlock] = useState<number | ''>('');
  const [walk, setWalk] = useState<PaintWalk | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [onlyToPaint, setOnlyToPaint] = useState(true);
  const [queue, setQueue] = useState(() => queuedPaintings());

  useEffect(() => subscribePaintingQueue(() => setQueue(queuedPaintings())), []);

  useEffect(() => {
    if (!hasAccess || farmId) return;
    colourService.walkPlaces()
      .then((res) => {
        if (!res.success) { setError(errorMessage(res, 'Could not load the farms')); return; }
        setPlaces(res.data);
        if (res.data.length === 1) setPickFarm(res.data[0].farm_id);
      })
      .catch((e) => setError(errorMessage(e, 'Could not load the farms')));
  }, [hasAccess, farmId]);

  const load = useCallback(async () => {
    if (!farmId) return;
    try {
      const res = await colourService.walk(farmId, blockId);
      if (res.success) setWalk(res.data);
      else setError(errorMessage(res, 'Could not load the rows'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the rows'));
    }
  }, [farmId, blockId]);

  useEffect(() => { if (hasAccess && farmId) load(); }, [hasAccess, farmId, load]);

  const waiting = [...queue.values()].filter((q) => !q.refused).length;
  useEffect(() => {
    if (!hasAccess || !farmId) return;
    const flush = () => flushPaintingQueue().then((r) => { if (r.sent) load(); }).catch(() => { /* tried again later */ });
    flush();
    window.addEventListener('online', flush);
    const timer = waiting ? window.setInterval(flush, 30000) : undefined;
    return () => { window.removeEventListener('online', flush); if (timer) window.clearInterval(timer); };
  }, [hasAccess, farmId, waiting, load]);

  const lots = useMemo(() => [...(walk?.lots ?? [])].sort(walkOrder), [walk]);
  const sections = useMemo(() => {
    const out: { key: string; title: string; lots: PaintWalkLot[] }[] = [];
    for (const l of lots) {
      const queuedAll = l.bands.every((b) => b.state === 'painted' || queue.get(`${l.lot_id}:${b.band}`));
      if (onlyToPaint && (!needsPaint(l) || queuedAll)) continue;
      const key = sectionKey(l);
      const last = out[out.length - 1];
      if (last?.key === key) last.lots.push(l);
      else out.push({ key, title: sectionTitle(l), lots: [l] });
    }
    return out;
  }, [lots, onlyToPaint, queue]);

  const counts = useMemo(() => {
    let toPaint = 0; let repaint = 0; let noColour = 0;
    for (const l of lots) {
      if (!l.bands.length) noColour++;
      for (const b of l.bands) {
        if (b.state === 'to_paint') toPaint++;
        if (b.state === 'repaint') repaint++;
      }
    }
    return { toPaint, repaint, noColour };
  }, [lots]);

  const paint = (lot: PaintWalkLot, band: LotBand) => async () => {
    const rec = await recordPainting(lot.lot_id, band.band);
    if (!rec) return; // queued; the button reads it from the queue
    setWalk((w) => w && ({
      ...w,
      lots: w.lots.map((l) => l.lot_id !== lot.lot_id ? l : {
        ...l,
        bands: l.bands.map((b) => b.band !== band.band ? b : { ...b, painting: rec, state: 'painted' }),
      }),
    }));
  };

  const undo = (band: LotBand) => async () => {
    if (!band.painting) return;
    const res = await colourService.voidPainting(band.painting.id);
    if (!res.success) throw new Error(errorMessage(res, 'Could not undo'));
    await load();
  };

  if (licenseLoading) {
    return <div className="max-w-2xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded-xl animate-pulse" /></div>;
  }
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Nursery seat required</h2>
          <p className="text-sm text-neutral-500">Painting needs a nursery operator, manager or admin seat.</p>
        </div>
      </div>
    );
  }

  if (!farmId) {
    const farm = places?.find((p) => p.farm_id === pickFarm);
    return (
      <>
        <PageHeader title="Painting" subtitle="Walk the rows and paint the bands" icon={<Paintbrush className="h-6 w-6 text-white" />} />
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
          {!places ? (!error && <div className="h-32 bg-neutral-100 rounded-xl animate-pulse" />) : places.length === 0 ? (
            <div className="bg-white border border-neutral-200 rounded-xl p-6 text-sm text-neutral-500">
              No lots are placed at a farm yet. Place lots on the Locations page first.
            </div>
          ) : (
            <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3" aria-label="Where to paint">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-sm text-neutral-700 min-w-0">
                  Farm
                  <select className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" value={pickFarm}
                    onChange={(e) => { setPickFarm(e.target.value ? Number(e.target.value) : ''); setPickBlock(''); }}>
                    <option value="">Choose a farm</option>
                    {places.map((p) => <option key={p.farm_id} value={p.farm_id}>{p.farm_name} ({p.lots} lots)</option>)}
                  </select>
                </label>
                <label className="text-sm text-neutral-700 min-w-0">
                  Block
                  <select className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" value={pickBlock}
                    disabled={!farm} onChange={(e) => setPickBlock(e.target.value ? Number(e.target.value) : '')}>
                    <option value="">Every block</option>
                    {farm?.blocks.map((b) => <option key={b.block_id} value={b.block_id}>{b.block_name} ({b.lots} lots)</option>)}
                  </select>
                </label>
              </div>
              <Button disabled={!pickFarm} onClick={() => {
                const next = new URLSearchParams({ farm: String(pickFarm) });
                if (pickBlock) next.set('block', String(pickBlock));
                setParams(next);
              }}>Start painting</Button>
            </section>
          )}
        </div>
      </>
    );
  }

  const where = walk ? (walk.block_name ? `${walk.farm_name} · ${walk.block_name}` : `${walk.farm_name} · every block`) : '';

  return (
    <>
      <PageHeader title="Painting" subtitle={where || 'Loading…'} icon={<Paintbrush className="h-6 w-6 text-white" />} />
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {!walk ? (!error && <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />) : (
          <>
            {!walk.enabled && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 break-words">
                Colour coding is switched off, so nobody else sees these colours. Switch it on under{' '}
                <Link to="/nursery/colour-codes" className="underline">Colour codes</Link>.
              </div>
            )}
            <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-1 text-sm text-neutral-700" aria-label="Progress">
              <p>
                {counts.toPaint} band{counts.toPaint === 1 ? '' : 's'} to paint
                {counts.repaint > 0 && ` · ${counts.repaint} to repaint in a new colour`}
              </p>
              {counts.noColour > 0 && (
                <p className="flex items-start gap-1.5 text-amber-800">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span className="break-words min-w-0">
                    {counts.noColour} lot{counts.noColour === 1 ? ' has' : 's have'} no colour yet. Give their lines one on{' '}
                    <Link to="/nursery/colour-codes" className="underline">Colour codes</Link>.
                  </span>
                </p>
              )}
              {waiting > 0 && (
                <p className="flex items-start gap-1.5 text-amber-800">
                  <CloudOff className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span className="break-words min-w-0">{waiting} waiting for signal on this phone. Keep the page open until they go.</span>
                </p>
              )}
            </section>
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
                <input type="checkbox" checked={onlyToPaint} onChange={(e) => setOnlyToPaint(e.target.checked)} />
                Only lots still to paint
              </label>
              <button type="button" className="text-sm text-primary-700 underline" onClick={() => setParams(new URLSearchParams())}>
                Somewhere else
              </button>
            </div>

            {sections.length === 0 && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800 flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                <span>{lots.length ? 'Every band here is painted.' : 'No lots are placed here.'}</span>
              </div>
            )}

            {sections.map((s) => (
              <section key={s.key} aria-label={s.title}>
                <h2 className="text-sm font-semibold text-neutral-700 break-words">{s.title}</h2>
                <ul className="mt-2 space-y-3">
                  {s.lots.map((l) => (
                    <li key={l.lot_id} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-neutral-500">{placeName(l)}</p>
                          <p className="font-semibold text-neutral-900 break-words">{treeName(l)}</p>
                          <p className="text-xs text-neutral-500 break-words">{l.tree_type_name}{l.lot_code ? ` · ${l.lot_code}` : ''}</p>
                        </div>
                        <p className="text-sm font-semibold text-neutral-900 flex-shrink-0">{l.current_count.toLocaleString()}</p>
                      </div>
                      {l.bands.length === 0 ? (
                        <p className="text-xs text-amber-800">This line has no colour yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {l.bands.map((b) => (
                            <BandButton
                              key={b.band}
                              band={b}
                              queued={queue.get(`${l.lot_id}:${b.band}`)}
                              tz={tz}
                              onPaint={paint(l, b)}
                              onUndo={undo(b)}
                            />
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </>
        )}
      </div>
    </>
  );
}

export default Painting;
